import { useEffect, useState } from 'react';
import MetricsFrameworkApiService from '../apiService/metricsFrameworkApiService';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import type { Filters, InsightResult } from '../types';
import { insightTileCache } from '../utils/tileCache';
import { getTileFetchStatusCode } from '../utils/tileFetchError';

const cacheKey = (id: string, filters: Filters, mockApiFlag: boolean, revision: number) =>
  `${id}:${filters.dealerId}:${filters.period.timePeriod}:${filters.period.startDate ?? ''}:${filters.period.endDate ?? ''}:mock=${mockApiFlag}:r${revision}`;

export const useInsight = (id: string, filters: Filters | null) => {
  const { mockApiFlag, dataRevision } = useMetricsFrameworkContext();
  const [data, setData] = useState<InsightResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusCode, setStatusCode] = useState<number | null>(null);

  useEffect(() => {
    if (!filters?.dealerId || !id) {
      setData(null);
      setIsLoading(false);
      setError('');
      setStatusCode(null);
      return;
    }

    const key = cacheKey(id, filters, mockApiFlag, dataRevision);
    const cached = insightTileCache.get(key);
    if (cached) {
      setData(cached);
      setIsLoading(false);
      setError('');
      setStatusCode(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError('');
      setStatusCode(null);
      try {
        const result = await MetricsFrameworkApiService.getInsight(id, filters, mockApiFlag);
        if (!cancelled) {
          insightTileCache.set(key, result);
          setData(result);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load insight';
          setError(message);
          setStatusCode(getTileFetchStatusCode(err) ?? null);
          setData(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [dataRevision, filters, id, mockApiFlag]);

  return { data, isLoading, error, statusCode };
};
